import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";

import "../../public/diermatch.css";
import { Icoon } from "../../public/DierMatchIcoon";
import { DierMatchKennismaking } from "./DierMatchKennismaking";
import { DierMatchMatch } from "./DierMatchMatch";
import {
  fetchOpvang,
  fetchOpvangen,
  fetchOpvangHuisstijl,
  type PublicHuisstijl,
  fetchBewaard,
  fetchPublication,
  fetchSearch,
  fetchSuccesverhalen,
  meldFavoriet,
  statusOf,
  type Facet,
  type PublicAnimalDetail,
  type PublicAnimalSummary,
  type PublicKenmerk,
  type PublicOpvang,
  type PublicSearchResult,
  type PublicSuccesverhaal,
  fetchKennismaking,
  type KennismakingLijst,
} from "../../public/publicApi";
import {
  detailPath,
  favorietenPath,
  isFavorietenPath,
  isMatchPath,
  matchPath,
  opvangPath,
  overviewPath,
  readOpvangFromPath,
  readSlugFromPath,
} from "../../public/diermatchRoute";
import { kenmerkLabel } from "../../public/kenmerkLabels";
import { useTelWeergave } from "../../public/weergaveTellen";

// De publieke DierMatch-adoptiesite (epic-7), herbouwd naar de aangeleverde mockup
// (diermatch-react.zip, maintainer 2026-08-24): een homepage met heroblok en zoekkaart, de
// vertrouwensstrook, de dierenkaarten met favorieten, "zo werkt adopteren" naast de aangesloten
// asielen, en de cijfers met een echt succesverhaal als citaat.
//
// Elke sectie draait op echte data of staat er niet. De mockup had een nieuwsbrief die niets
// verstuurde en een inlogknop zonder account erachter — allebei weggelaten: een formulier dat doet
// alsof is erger dan geen formulier. De zoekkaart biedt alleen filters die de server echt kent
// (diersoort, leeftijd, plaats+straal, zoekterm); "grootte" uit de mockup bestaat niet in de data.
//
// Navigatie is client-side (history pushState) zodat een diepe link naar /dieren/<slug> direct op
// dat detail uitkomt en de terugknop werkt.

const PAGE_SIZE = 10;

/**
 * De heldenfoto van het merk, uit het aangeleverde concept.
 *
 * <p>Vast beeld, niet het aanbod: een hero die per bezoek een ander dier laat zien verandert bij elke
 * plaatsing van vorm, en het is bovendien geen aanbieding — er staat geen naam bij en geen knop om
 * hem te leren kennen. De echte dieren staan op de kaarten, en daar hoort ook alleen echt aanbod.
 */
const HERO_FOTO = '/diermatch-hero.jpg';
/** De brede band voor de kop van een opvangpagina; aangeleverd beeld, liggend en met veel groen. */
const OPVANG_BAND = '/diermatch-opvang-band.jpg';

/** De facetten die de zoekdienst altijd meestuurt; de rest komt uit de kenmerkencatalogus. */
const VASTE_FACETTEN = ["ras", "geslacht", "leeftijd", "opvang"];

type Route =
  | { view: "overview" }
  | { view: "detail"; slug: string }
  /** De pagina van één opvang; `naam` alleen voor de leesbare staart van de link. */
  | { view: "opvang"; sleutel: string; naam?: string | null }
  /** De AI-matchwizard (epic 12) op `/dieren/match`. */
  | { view: "match" }
  /** De bewaarde dieren van deze bezoeker, op `/dieren/favorieten`. */
  | { view: "favorieten" };

function routeFromPath(pathname: string): Route {
  if (isMatchPath(pathname)) {
    return { view: "match" };
  }
  if (isFavorietenPath(pathname)) {
    return { view: "favorieten" };
  }
  const sleutel = readOpvangFromPath(pathname);
  if (sleutel) {
    return { view: "opvang", sleutel };
  }
  const slug = readSlugFromPath(pathname);
  return slug ? { view: "detail", slug } : { view: "overview" };
}

function pathVoor(route: Route, hostname: string): string {
  switch (route.view) {
    case "detail":
      return detailPath(route.slug);
    case "opvang":
      return opvangPath(route.sleutel, route.naam);
    case "match":
      return matchPath();
    case "favorieten":
      return favorietenPath();
    default:
      return overviewPath(hostname);
  }
}
// ── Favorieten ───────────────────────────────────────────────────────────────────────────────────
// Volledig client-side (localStorage): de site kent geen accounts, dus dit is een geheugensteun op
// dít toestel en meer belooft de knop ook niet. De sleutel draagt een versie voor als de vorm wijzigt.

const FAVORIETEN_SLEUTEL = "diermatch.favorieten.v1";

function leesFavorieten(): string[] {
  try {
    const ruw = globalThis.localStorage?.getItem(FAVORIETEN_SLEUTEL);
    const lijst = ruw ? JSON.parse(ruw) : [];
    return Array.isArray(lijst) ? lijst.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function scrollNaar(id: string) {
  const doel = document.getElementById(id);
  if (doel && typeof doel.scrollIntoView === "function") {
    doel.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

function bewaarFavorieten(slugs: string[]): void {
  try {
    globalThis.localStorage?.setItem(FAVORIETEN_SLEUTEL, JSON.stringify(slugs));
  } catch {
    // Vol of geblokkeerd: dan onthoudt de browser het gewoon niet. Geen reden om iets te tonen.
  }
}


/** Het compacte pootmerkteken uit het oorspronkelijke DierMatch-concept. */
function MerkTeken() {
  return (
    <span className="dm-merk-teken" aria-hidden="true">
      <img src="/brand-paw-circle.svg" alt="" />
    </span>
  );
}

/** A soft paw glyph for photo placeholders (no external asset — inline, tintable via currentColor). */
function PawGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 14.5c-2.3 0-4.2 1.6-4.2 3.6 0 1.3 1 2.1 2.4 2.1.7 0 1.2-.2 1.8-.4.6.2 1.1.4 1.8.4 1.4 0 2.4-.8 2.4-2.1 0-2-1.9-3.6-4.2-3.6Z" />
      <ellipse cx="6.5" cy="11" rx="1.7" ry="2.2" />
      <ellipse cx="17.5" cy="11" rx="1.7" ry="2.2" />
      <ellipse cx="9.5" cy="7.5" rx="1.6" ry="2.1" />
      <ellipse cx="14.5" cy="7.5" rx="1.6" ry="2.1" />
    </svg>
  );
}

/** A photo that gracefully falls back to a paw placeholder when the src is absent or fails to load. */
function Photo({ src, alt }: { src: string | null; alt: string }) {
  const [failed, setFailed] = useState(false);
  const show = src && !failed;
  return (
    <>
      {show && <img src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} />}
      {!show && (
        <div className="dm-photo-placeholder">
          <PawGlyph />
        </div>
      )}
    </>
  );
}

/** Het merkje: het hartje met de merknaam, op meerdere plekken in dezelfde vorm. */
function Merk({ compact = false }: { compact?: boolean }) {
  return (
    <span className={compact ? "dm-merk dm-merk--compact" : "dm-merk"}>
      <MerkTeken />
      <span className="dm-merk-naam"><strong>diermatch</strong><em>.nl</em></span>
    </span>
  );
}

export function DierMatchSite() {
  const hostname = globalThis.location?.hostname ?? "";
  const [route, setRoute] = useState<Route>(() =>
    routeFromPath(globalThis.location?.pathname ?? overviewPath(hostname)),
  );
  const [favorieten, setFavorieten] = useState<string[]>(leesFavorieten);
  /**
   * Waar "terug" vanaf een dier heen gaat.
   *
   * <p>De opvangpagina, de matchlijst of je favorieten als je daar vandaan kwam, anders de home. Zonder de
   * matchlijst erbij belandde je na het bekijken van één match op de homepagina, en moest je de
   * hele vragenlijst opnieuw langs om de andere negen te zien — precies de reden om een top 10 te
   * maken was dat je ze kunt vergelijken.
   */
  const [herkomst, setHerkomst] =
    useState<Extract<Route, { view: "opvang" } | { view: "match" } | { view: "favorieten" }> | null>(null);

  useEffect(() => {
    const onPop = () => setRoute(routeFromPath(globalThis.location?.pathname ?? overviewPath(hostname)));
    globalThis.addEventListener?.("popstate", onPop);
    return () => globalThis.removeEventListener?.("popstate", onPop);
  }, [hostname]);

  const navigate = useCallback((next: Route) => {
    globalThis.history?.pushState?.(null, "", pathVoor(next, hostname));
    setRoute(next);
    globalThis.scrollTo?.(0, 0);
  }, [hostname]);

  const goHome = useCallback(() => navigate({ view: "overview" }), [navigate]);

  /**
   * De kale stand voor een `iframe` op de site van een asiel: geen kop, geen voet — alleen het aanbod.
   * Uit de URL, zoals op ikzoekbaas (8-8), en alleen op de opvangpagina: de rest van DierMatch mag niet
   * ingekaderd worden en heeft er dus ook niets aan.
   */
  const insluiten =
    route.view === "opvang"
    && new URLSearchParams(globalThis.location?.search ?? "").get("insluiten") === "1";

  /** Naar een sectie op de homepage; vanaf een andere pagina eerst terug naar huis. */
  const naarSectie = useCallback((id: string) => {
    if (routeFromPath(globalThis.location?.pathname ?? "").view !== "overview") {
      navigate({ view: "overview" });
    }
    globalThis.setTimeout?.(() => scrollNaar(id), 60);
  }, [navigate]);

  const openDier = useCallback((slug: string) => {
    if (insluiten) {
      // In het kadertje op de site van het asiel opent een dier in een eigen tabblad: de dierpagina
      // mag zelf niet ingekaderd worden, en het contactblok hoort niet in een strook van 600 pixels.
      globalThis.open?.(detailPath(slug), "_blank", "noopener");
      return;
    }
    setHerkomst(
      route.view === "opvang" || route.view === "match" || route.view === "favorieten"
        ? route
        : null,
    );
    navigate({ view: "detail", slug });
  }, [insluiten, navigate, route]);

  const openOpvang = useCallback((opvang: PublicOpvang) => {
    navigate({ view: "opvang", sleutel: opvang.sleutel, naam: opvang.naam });
  }, [navigate]);

  /**
   * Een hartje aan of uit.
   *
   * <p>Buiten de state-updater gerekend en niet erin: React roept zo'n updater in StrictMode twee
   * keer aan, en dan zou het asiel er twee belangstellenden bij krijgen voor één klik. Een updater
   * hoort alleen te rekenen.
   */
  const wisselFavoriet = useCallback((slug: string) => {
    const bewaard = !favorieten.includes(slug);
    const volgende = bewaard ? [...favorieten, slug] : favorieten.filter((s) => s !== slug);
    bewaarFavorieten(volgende);
    setFavorieten(volgende);
    // Het asiel mag zien hoeveel mensen zijn dier bewaard hebben. Alleen het getal gaat mee; wie
    // het was blijft hier. Niet afwachten: het hartje hoort meteen te reageren.
    void meldFavoriet(slug, bewaard);
  }, [favorieten]);

  /** De bewaarde lijst terugbrengen tot wat de server nog kent. */
  const snoeiFavorieten = useCallback((nog: string[]) => {
    bewaarFavorieten(nog);
    setFavorieten(nog);
  }, []);

  /**
   * Bij het laden opruimen wat er niet meer is.
   *
   * <p>Een favoriet is een slug in deze browser, en die blijft staan ook als het dier allang een
   * thuis heeft. De server geeft alleen terug wat nog {@code live} is, dus wat ontbreekt is
   * geplaatst of ingetrokken en gaat eruit — anders telt de teller dieren die niemand meer kan
   * adopteren.
   *
   * <p>Eén aanroep bij het laden, niet bij elke klik: `wisselFavoriet` weet zelf wat het toevoegt,
   * en dat is per definitie een dier dat de bezoeker net op het scherm had.
   */
  useEffect(() => {
    const bewaardeSlugs = leesFavorieten();
    if (bewaardeSlugs.length === 0) {
      return;
    }
    let levend = true;
    fetchBewaard(bewaardeSlugs, "DIERMATCH")
      .then((dieren) => {
        if (!levend) return;
        const nog = dieren.map((d) => d.slug);
        if (nog.length !== bewaardeSlugs.length) snoeiFavorieten(nog);
      })
      .catch(() => {
        // Onbereikbaar: dan laten we de bewaarde slugs met rust. Een netwerkfout is geen bewijs
        // dat een dier geplaatst is, en wie offline is hoort zijn lijstje niet kwijt te raken.
      });
    return () => {
      levend = false;
    };
  }, [snoeiFavorieten]);

  return (
    <div className={insluiten ? "diermatch diermatch--ingesloten" : "diermatch"}>
      {!insluiten && (
      <header className="diermatch-header">
        <div className="dm-shell dm-header-inner">
          <button className="diermatch-brand" onClick={goHome} aria-label="DierMatch home">
            <Merk />
          </button>
          <nav className="diermatch-nav" aria-label="Hoofdnavigatie">
            <button onClick={() => naarSectie("dieren")}>Dieren <ChevronDown width={13} height={13} strokeWidth={1.8} aria-hidden="true" /></button>
            <button onClick={() => naarSectie("asielen")}>Asielen</button>
            <button onClick={() => naarSectie("hoe")}>Hoe het werkt</button>
            <button onClick={() => naarSectie("over")}>Over ons</button>
          </nav>
          <div className="dm-header-acties">
            <button
              type="button"
              className="dm-favorieten-teller"
              onClick={() => navigate({ view: "favorieten" })}
              aria-label={`${favorieten.length} favorieten bekijken`}
            >
              <Icoon naam="hart" size={19} /> Favorieten <b>{favorieten.length}</b>
            </button>
            <button className="dm-asiel-cta" onClick={() => naarSectie("asielen")}>Voor asielen</button>
          </div>
        </div>
      </header>
      )}

      <main className="diermatch-main">
        {route.view === "overview" && (
          <Overview
            onOpen={openDier}
            onOpvang={openOpvang}
            onMatch={() => navigate({ view: "match" })}
            favorieten={favorieten}
            onFavoriet={wisselFavoriet}
          />
        )}
        {route.view === "opvang" && (
          <OpvangPagina
            sleutel={route.sleutel}
            insluiten={insluiten}
            favorieten={favorieten}
            onFavoriet={wisselFavoriet}
            onOpen={openDier}
            onHome={goHome}
          />
        )}
        {route.view === "detail" && (
          <Detail
            slug={route.slug}
            onBack={herkomst ? () => navigate(herkomst) : goHome}
            favoriet={favorieten.includes(route.slug)}
            onFavoriet={() => wisselFavoriet(route.slug)}
          />
        )}
        {route.view === "favorieten" && (
          <FavorietenPagina
            favorieten={favorieten}
            onFavoriet={wisselFavoriet}
            onOpvallen={snoeiFavorieten}
            onOpen={openDier}
            onHome={goHome}
          />
        )}
        {route.view === "match" && (
          <DierMatchMatch onTerug={() => naarSectie("dieren")} onDier={openDier} />
        )}
      </main>

      {!insluiten && (
      <footer className="diermatch-footer" id="over">
        <div className="dm-shell dm-footer-grid">
          <div className="dm-footer-merk">
            <Merk compact />
            <p>Het landelijke platform voor asieldieren en de mensen die van hen houden.</p>
          </div>
          <div>
            <h3>DierMatch</h3>
            <button onClick={() => naarSectie("dieren")}>Dieren</button>
            <button onClick={() => naarSectie("asielen")}>Asielen</button>
            <button onClick={() => naarSectie("hoe")}>Hoe het werkt</button>
          </div>
          <div>
            <h3>Adopteren</h3>
            <p className="dm-footer-tekst">
              Alle dieren worden aangeboden door aangesloten asielen. Kennismaken en adopteren gaat
              altijd via het asiel zelf — zij kennen het dier het beste.
            </p>
          </div>
          <div className="dm-footer-asiel">
            <span className="dm-footer-asiel-icoon"><Icoon naam="huis" size={20} /></span>
            <div><h3>Ook dieren plaatsen?</h3><p>Laat jouw opvang en dieren herkenbaar terugkomen op DierMatch.</p></div>
            <button onClick={() => naarSectie("asielen")}>Meer voor asielen</button>
          </div>
        </div>
        <div className="dm-shell dm-footer-onder">
          <span>© {new Date().getFullYear()} DierMatch.nl</span>
          <span>Met aandacht gebouwd voor dier én mens.</span>
        </div>
      </footer>
      )}
    </div>
  );
}

// ── Overview (homepage) ──────────────────────────────────────────────────────────────────────────

interface Zoekkeuze {
  groep: string;
  leeftijd: string;
  postcode: string;
  q: string;
}

const LEGE_KEUZE: Zoekkeuze = { groep: "", leeftijd: "", postcode: "", q: "" };

function Overview({
  onOpen,
  onOpvang,
  onMatch,
  favorieten,
  onFavoriet,
}: {
  onMatch: () => void;
  onOpen: (slug: string) => void;
  onOpvang: (opvang: PublicOpvang) => void;
  favorieten: string[];
  onFavoriet: (slug: string) => void;
}) {
  // De ingestuurde zoekopdracht — niet wat er in de velden staat: de kaart zoekt op de knop, zoals de
  // mockup, zodat het typen van een postcode niet halverwege al verzoeken afvuurt.
  const [keuze, setKeuze] = useState<Zoekkeuze>(LEGE_KEUZE);
  const [resultaat, setResultaat] = useState<PublicSearchResult | null>(null);
  const [items, setItems] = useState<PublicAnimalSummary[]>([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [phase, setPhase] = useState<"loading" | "error" | "ready">("loading");
  const [opvangen, setOpvangen] = useState<PublicOpvang[]>([]);
  // Er passen er vier, maar er zijn er twintig. Ze draaien daarom door, zodat elk asiel aan de beurt
  // komt in plaats van dat de eerste vier het hele netwerk vertegenwoordigen.
  const [asielStart, setAsielStart] = useState(0);
  const [draaitStil, setDraaitStil] = useState(false);
  const [verhalen, setVerhalen] = useState<PublicSuccesverhaal[]>([]);

  /**
   * Vier asielen tegelijk, elke zes seconden de volgende vier. Stilzetten zodra de muis erop staat of
   * er iets de focus heeft: wie een naam leest of op het punt staat te klikken, moet niet zien
   * wegdraaien waar hij naar wijst. Wie liever geen beweging heeft (prefers-reduced-motion) krijgt
   * gewoon de eerste vier.
   */
  const asielenPerSlag = 4;
  const zichtbareOpvangen = useMemo(() => {
    if (opvangen.length <= asielenPerSlag) return opvangen;
    return Array.from({ length: asielenPerSlag }, (_, i) => opvangen[(asielStart + i) % opvangen.length]);
  }, [opvangen, asielStart]);

  useEffect(() => {
    if (opvangen.length <= asielenPerSlag || draaitStil) return;
    if (globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const t = setInterval(() => setAsielStart((i) => (i + asielenPerSlag) % opvangen.length), 6000);
    return () => clearInterval(t);
  }, [opvangen.length, draaitStil]);

  useEffect(() => {
    document.title = "Vind jouw nieuwe maatje · DierMatch";
  }, []);

  const load = useCallback(async (z: Zoekkeuze, index: number, append: boolean) => {
    if (!append) setPhase("loading");
    try {
      const r = await fetchSearch({
        kanaal: "DIERMATCH",
        groep: z.groep || undefined,
        leeftijd: z.leeftijd ? [z.leeftijd] : undefined,
        // Plaats of postcode; de straal is bewust ruim — wie een plaats intikt bedoelt "in de buurt".
        postcode: z.postcode || undefined,
        straal: z.postcode ? 50 : undefined,
        q: z.q || undefined,
        // Gereserveerde dieren blijven staan, met een lint; beschikbare komen eerst (zoekdienst sorteert zo).
        alleenBeschikbaar: false,
        page: index,
        size: PAGE_SIZE,
      });
      setResultaat(r);
      setItems((prev) => (append ? [...prev, ...r.items] : r.items));
      setPageIndex(index);
      setPhase("ready");
    } catch {
      setPhase("error");
    }
  }, []);

  useEffect(() => {
    void load(keuze, 0, false);
  }, [keuze, load]);

  useEffect(() => {
    // Sfeer, geen kritiek pad: als deze twee stukken niet laden staat er gewoon minder op de pagina.
    fetchOpvangen("DIERMATCH").then(setOpvangen).catch(() => {});
    fetchSuccesverhalen("DIERMATCH", 1).then(setVerhalen).catch(() => {});
  }, []);

  const totaal = resultaat?.totalElements ?? 0;
  const hasMore = resultaat ? items.length < resultaat.totalElements : false;

  return (
    <>
      <section className="dm-hero dm-shell" id="top">
        <div className="dm-hero-copy">
          <h1>Vind jouw<br /><span>nieuwe maatje</span><span className="dm-hero-hart"><Icoon naam="hart" size={34} /></span></h1>
          <p>Ontdek asieldieren van betrouwbare asielen uit heel Nederland op één plek.</p>
          {/* De tweede ingang (epic 12): naast zelf filteren ook een paar vragen laten stellen. Naast
              de zoekkaart en niet ervoor — wie weet wat hij zoekt moet niet eerst langs een wizard. */}
          <button type="button" className="dm-hero-match" onClick={onMatch}>
            <span className="dm-hero-match-icoon"><Icoon naam="match" size={21} /></span>
            <span className="dm-hero-match-tekst">
              <strong>Weet je nog niet wat bij je past?</strong>
              <small>Beantwoord een paar vragen en zie welke dieren het beste bij jou passen.</small>
            </span>
            <span className="dm-hero-match-pijl"><Icoon naam="pijlRechts" size={18} /></span>
          </button>
        </div>
        <div className="dm-hero-visual" aria-hidden="true">
          <img className="dm-hero-foto" src={HERO_FOTO} alt="" />
          <div className="dm-hero-badge">Een match voor het leven</div>
        </div>
        <Zoekkaart
          groepen={resultaat?.groepen ?? []}
          facetten={resultaat?.facetten ?? []}
          totaal={totaal}
          onZoek={(z) => {
            setKeuze(z);
            globalThis.setTimeout?.(
              () => scrollNaar("dieren"),
              60,
            );
          }}
        />
      </section>

      <section className="dm-trust dm-shell">
        {[
          ["schild", "Betrouwbare asielen", "Elk dier wordt aangeboden door een aangesloten asiel."],
          ["stethoscoop", "Medisch gecontroleerd", "Gezondheid en medische informatie komen rechtstreeks van het asiel."],
          ["match", "Persoonlijke match", "Compacte kenmerken helpen je een dier te vinden dat echt bij je past."],
          ["nazorg", "Nazorg & support", "Ook na een adoptie blijft het asiel jouw eerste aanspreekpunt."],
        ].map(([icoon, titel, tekst]) => (
          <div className="dm-trust-item" key={titel}>
            <span className="dm-trust-icoon"><Icoon naam={icoon} size={31} /></span>
            <div><strong>{titel}</strong><p>{tekst}</p></div>
          </div>
        ))}
      </section>

      <section className={`dm-dieren dm-shell ${phase === "ready" && items.length === 0 ? "dm-dieren--leeg" : ""}`} id="dieren">
        <div className="dm-sectiekop">
          <div>
            <span className="dm-eyebrow">Klaar voor een ontmoeting?</span>
            <h2>Dieren die een thuis zoeken</h2>
          </div>
          {totaal > 0 && <span className="dm-teller">{totaal} {totaal === 1 ? "dier zoekt" : "dieren zoeken"} een thuis</span>}
        </div>

        {phase === "loading" && (
          <div className="diermatch-skeleton" aria-hidden="true">
            {Array.from({ length: 5 }, (_, i) => <div className="dm-skeleton-card" key={i} />)}
          </div>
        )}
        {phase === "error" && (
          <p className="dm-melding">De dieren konden niet worden geladen. Probeer het zo weer.</p>
        )}
        {phase === "ready" && items.length === 0 && (
          <div className="dm-leeg">
            <span className="dm-leeg-icoon"><Icoon naam="poot" size={31} /></span>
            <div>
              <h3>{Object.values(keuze).some(Boolean) ? "Nog geen passende match gevonden" : "De eerste matches verschijnen hier binnenkort"}</h3>
              <p>{Object.values(keuze).some(Boolean)
                ? "Probeer de filters iets ruimer te zetten. Nieuwe dieren worden rechtstreeks door aangesloten asielen geplaatst."
                : "Zodra een aangesloten asiel een dier op DierMatch publiceert, staat het hier met een compact profiel en duidelijke kenmerken."}</p>
            </div>
            {Object.values(keuze).some(Boolean) && (
              <button type="button" onClick={() => setKeuze(LEGE_KEUZE)}>Wis filters</button>
            )}
          </div>
        )}

        {items.length > 0 && (
          <div className="diermatch-grid">
            {items.map((animal) => (
              <AnimalCard
                key={animal.platformId}
                animal={animal}
                favoriet={favorieten.includes(animal.slug)}
                onFavoriet={() => onFavoriet(animal.slug)}
                onOpen={() => onOpen(animal.slug)}
              />
            ))}
          </div>
        )}

        {hasMore && (
          <div className="dm-meer">
            <button className="dm-meer-knop" onClick={() => void load(keuze, pageIndex + 1, true)}>
              Meer dieren laden
            </button>
          </div>
        )}
      </section>

      <section className="dm-split dm-shell" id="hoe">
        <div className="dm-hoe-kaart">
          <span className="dm-eyebrow">Eenvoudig en persoonlijk</span>
          <h2>Zo werkt adopteren</h2>
          <div className="dm-stappen">
            {[
              ["1", "zoek", "Zoek & vind", "Gebruik de filters en vind dieren die bij jou passen."],
              ["2", "praatwolk", "Neem contact op", "Stel vragen of plan een kennismaking met het asiel."],
              ["3", "huis", "Adopteer & geniet", "Na een positieve match mag jouw nieuwe maatje mee naar huis."],
            ].map(([nr, icoon, titel, tekst], i) => (
              <div className="dm-stap-wrap" key={nr}>
                {i > 0 && <div className="dm-staplijn" aria-hidden="true" />}
                <div className="dm-stap">
                  <b aria-hidden="true">{nr}</b>
                  <span className="dm-stap-icoon"><Icoon naam={icoon} size={34} /></span>
                  <h3>{titel}</h3>
                  <p>{tekst}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="dm-asiel-kaart" id="asielen">
          <span className="dm-eyebrow">Landelijk netwerk</span>
          <h2>Asielen op DierMatch</h2>
          <p>
            DierMatch werkt samen met opvangorganisaties door heel Nederland. Elk dier staat er
            namens zijn eigen asiel — dat is ook wie je spreekt bij een kennismaking.
          </p>
          {opvangen.length > 0 ? (
            <div
              className="dm-asielen"
              onMouseEnter={() => setDraaitStil(true)}
              onMouseLeave={() => setDraaitStil(false)}
              onFocusCapture={() => setDraaitStil(true)}
              onBlurCapture={() => setDraaitStil(false)}
            >
              {zichtbareOpvangen.map((o) => (
                <AsielMini key={o.sleutel} opvang={o} onOpvang={onOpvang} />
              ))}
            </div>
          ) : (
            <div className="dm-asiel-leeg">
              <span><Icoon naam="huis" size={30} /></span>
              <div><strong>Hier komen de aangesloten asielen</strong><p>Je ziet straks per organisatie direct de plaats en hoeveel dieren er een thuis zoeken.</p></div>
            </div>
          )}
        </div>
      </section>

      <section className={verhalen[0] ? "dm-cijfers dm-shell" : "dm-cijfers dm-cijfers--solo dm-shell"}>
        <div className="dm-cijfer-grid">
          {/* Alleen cijfers die het platform echt kent — een verzonnen "12.847 adopties" haalt de */}
          {/* geloofwaardigheid weg bij de cijfers die wél kloppen. */}
          <div className="dm-cijfer"><span className="dm-cijfer-icoon"><Icoon naam="poot" size={29} /></span><strong>{totaal > 0 ? totaal : "Vind"}</strong><small>{totaal > 0 ? `${totaal === 1 ? "dier zoekt" : "dieren zoeken"} een thuis` : "jouw nieuwe maatje"}</small></div>
          <div className="dm-cijfer"><span className="dm-cijfer-icoon"><Icoon naam="huis" size={29} /></span><strong>{opvangen.length > 0 ? opvangen.length : "Direct"}</strong><small>{opvangen.length > 0 ? `aangesloten ${opvangen.length === 1 ? "asiel" : "asielen"}` : "contact met het asiel"}</small></div>
          <div className="dm-cijfer"><span className="dm-cijfer-icoon"><Icoon naam="hart" size={29} /></span><strong>{favorieten.length > 0 ? favorieten.length : "Bewaar"}</strong><small>{favorieten.length > 0 ? "jouw favorieten" : "dieren als favoriet"}</small></div>
        </div>
        {verhalen[0] && (
          <blockquote className="dm-citaat">
            {verhalen[0].fotoUrl && (
              <span className="dm-citaat-foto"><img src={verhalen[0].fotoUrl} alt={verhalen[0].fotoAlt ?? verhalen[0].naam} /></span>
            )}
            <p>{verhalen[0].verhaal}</p>
            <cite>— {verhalen[0].naam}{verhalen[0].opvangNaam ? `, via ${verhalen[0].opvangNaam}` : ""}</cite>
          </blockquote>
        )}
      </section>
    </>
  );
}

/** De initialen voor het asiel-logootje: "Dierenopvang Brabant" → "DB". */
/**
 * Eén asiel in het landelijke blok. Het logo komt van de opvang zelf
 * ({@code /api/public/opvangen/{sleutel}/logo}); heeft die er geen, dan geeft dat endpoint 404 en
 * vallen we terug op de initialen. Vandaar het onError-vangnet in plaats van vooraf vragen of er een
 * logo is: dat scheelt een verzoek per asiel, en de terugval is toch nodig.
 */
function AsielMini({ opvang, onOpvang }: { opvang: PublicOpvang; onOpvang: (o: PublicOpvang) => void }) {
  const [logoStuk, setLogoStuk] = useState(false);
  const logo = `/api/public/opvangen/${encodeURIComponent(opvang.sleutel)}/logo`;
  return (
    <button type="button" className="dm-asiel-mini" onClick={() => onOpvang(opvang)}>
      <span className="dm-asiel-logo" aria-hidden="true">
        {logoStuk
          ? initialen(opvang.naam)
          : <img src={logo} alt="" loading="lazy" onError={() => setLogoStuk(true)} />}
      </span>
      <div className="dm-asiel-mini__tekst">
        <strong>{opvang.naam ?? "Opvang"}</strong>
        {opvang.plaats && <small>{opvang.plaats}</small>}
        <em>{opvang.aantal} {opvang.aantal === 1 ? "dier" : "dieren"}</em>
      </div>
    </button>
  );
}

function initialen(naam: string | null): string {
  if (!naam) return "•";
  return naam.split(/\s+/).filter((w) => /^[A-ZÀ-Ž]/.test(w)).slice(0, 2).map((w) => w[0]).join("") || naam[0].toUpperCase();
}

function Zoekkaart({
  groepen,
  facetten,
  totaal,
  onZoek,
}: {
  groepen: PublicSearchResult["groepen"];
  facetten: Facet[];
  totaal: number;
  onZoek: (keuze: Zoekkeuze) => void;
}) {
  const [groep, setGroep] = useState("");
  const [leeftijd, setLeeftijd] = useState("");
  const [postcode, setPostcode] = useState("");
  const [q, setQ] = useState("");
  const leeftijden = facetten.find((f) => f.sleutel === "leeftijd")?.opties ?? [];

  return (
    <form
      className="dm-zoekkaart"
      onSubmit={(e) => {
        e.preventDefault();
        onZoek({ groep, leeftijd, postcode: postcode.trim(), q: q.trim() });
      }}
    >
      <label>
        <span>Diersoort</span>
        <div className="dm-select"><i><Icoon naam="poot" size={17} /></i>
          <select value={groep} onChange={(e) => setGroep(e.target.value)} aria-label="Diersoort">
            <option value="">Alle diersoorten</option>
            {groepen.map((g) => <option key={g.pad} value={g.pad}>{g.label} ({g.aantal})</option>)}
          </select>
        </div>
      </label>
      <label>
        <span>Locatie</span>
        <div className="dm-select"><i><Icoon naam="speld" size={17} /></i>
          <input
            value={postcode}
            onChange={(e) => setPostcode(e.target.value)}
            placeholder="Plaats of postcode"
            aria-label="Plaats of postcode"
          />
        </div>
      </label>
      <label>
        <span>Leeftijd</span>
        <div className="dm-select"><i><Icoon naam="kalender" size={17} /></i>
          <select value={leeftijd} onChange={(e) => setLeeftijd(e.target.value)} disabled={leeftijden.length === 0} aria-label="Leeftijd">
            <option value="">Alle leeftijden</option>
            {leeftijden.map((o) => <option key={o.waarde} value={o.waarde}>{o.label} ({o.aantal})</option>)}
          </select>
        </div>
      </label>
      <label>
        <span>Zoekterm</span>
        <div className="dm-select"><i><Icoon naam="zoek" size={17} /></i>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Naam of ras" aria-label="Zoek op naam of ras" />
        </div>
      </label>
      <button className="dm-zoekknop" type="submit"><Icoon naam="zoek" size={17} /> Zoek dieren</button>
      <div className="dm-zoekmeta">
        <Icoon naam="poot" size={15} /> {totaal} {totaal === 1 ? "dier wacht" : "dieren wachten"} op een liefdevol thuis
        <span className="dm-zoekhint"><Icoon naam="hart" size={14} /> Foto's en uitgebreide profielen per dier</span>
      </div>
    </form>
  );
}

function AnimalCard({
  animal,
  favoriet,
  onFavoriet,
  onOpen,
}: {
  animal: PublicAnimalSummary;
  favoriet: boolean;
  onFavoriet: () => void;
  onOpen: () => void;
}) {
  const meta = [animal.soort, animal.geslacht, animal.leeftijd].filter(Boolean);
  return (
    <article className="diermatch-card">
      <div className="dm-photo-frame">
        {/* De foto en de tekst openen het dier; het hart is een eigen knop en mag het openen niet
            meenemen — anders belandt elke favoriet per ongeluk op de detailpagina. */}
        <button className="dm-photo-open" onClick={onOpen} aria-label={`Bekijk ${animal.naam}`}>
          <Photo src={animal.hoofdfotoUrl} alt={animal.hoofdfotoAlt ?? animal.naam} />
        </button>
        {nietBeschikbaar(animal.beschikbaarheid) && (
          <span className="dm-status-lint">{animal.beschikbaarheid}</span>
        )}
        <button
          type="button"
          className={favoriet ? "dm-hart is-actief" : "dm-hart"}
          onClick={onFavoriet}
          aria-pressed={favoriet}
          aria-label={`${animal.naam} als favoriet`}
        >
          {favoriet ? "♥" : "♡"}
        </button>
      </div>
      <button className="dm-body" onClick={onOpen}>
        <p className="dm-name">{animal.naam}</p>
        {meta.length > 0 && (
          <p className="dm-sub">{meta.map((m, i) => (
            <span key={i}>{i > 0 && <span className="dm-punt" aria-hidden="true"> • </span>}{m}</span>
          ))}</p>
        )}
        {animal.tags.length > 0 && (
          <span className="diermatch-tags">
            {animal.tags.slice(0, 3).map((tag) => (
              <span key={tag} className="diermatch-tag">{tag}</span>
            ))}
          </span>
        )}
        {(animal.opvangNaam || animal.plaats) && (
          <small className="dm-opvang"><Icoon naam="speld" size={13} /> {[animal.opvangNaam, animal.plaats].filter(Boolean).join(", ")}</small>
        )}
      </button>
    </article>
  );
}

// ── De pagina van één opvang ─────────────────────────────────────────────────────────────────────

/**
 * Het aanbod van één asiel op een eigen adres — de DierMatch-tegenhanger van story 8-8 op ikzoekbaas.
 *
 * <p>Een asiel zet deze link op zijn eigen site, of de kale variant (`?insluiten=1`) in een iframe. De
 * sleutel is dezelfde als op ikzoekbaas; het aanbod is dat van het DierMatch-kanaal. Dat maakt het
 * verschil voor een asiel dat alleen voor DierMatch kiest: dat staat niet op ikzoekbaas en had daar
 * dus ook nooit een pagina.
 *
 * <p>Een sleutel die niet bestaat geeft een nette melding. Geen zeldzaam geval: deze links staan jaren
 * op andermans site, en een opvang zonder dieren op DierMatch staat niet in de lijst.
 */
/**
 * Het getekende ringetje om de teller, met de streepjes rechtsboven — zoals in het ontwerp.
 *
 * <p>Een svg en geen border: de ring hoort niet helemaal rond te lopen en iets scheef te staan, zodat
 * hij met de hand getrokken lijkt. Dat krijg je met een rand nooit voor elkaar.
 */
function TellerRand() {
  return (
    <svg className="dm-opvang-hero__rand" viewBox="0 0 160 160" aria-hidden="true">
      <g fill="none" stroke="#f6f9f0" strokeLinecap="round">
        <path d="M112 15a72 72 0 1 1-72 6" strokeWidth="8" opacity=".95" />
        <path d="M112 15a72 72 0 1 1-72 6" strokeWidth="5" opacity=".7" transform="rotate(124 80 80)" />
      </g>
      <g stroke="#fff" strokeWidth="5.5" strokeLinecap="round">
        <path d="M134 23 147 10" />
        <path d="M143 38 159 33" />
        <path d="M122 12 125 0" />
      </g>
    </svg>
  );
}

/**
 * De bewaarde dieren van deze bezoeker.
 *
 * <p>De lijst staat in de browser als slugs; de dieren erbij komen van de server, en alleen de
 * dieren die er nog zijn komen terug. Wat ontbreekt is geplaatst of ingetrokken en wordt hier ook
 * uit het lijstje gehaald — een favoriet die niemand meer kan adopteren is geen favoriet.
 */
function FavorietenPagina({
  favorieten,
  onFavoriet,
  onOpvallen,
  onOpen,
  onHome,
}: {
  favorieten: string[];
  onFavoriet: (slug: string) => void;
  /** Slugs die er niet meer zijn; de site haalt ze uit de bewaarde lijst. */
  onOpvallen: (nog: string[]) => void;
  onOpen: (slug: string) => void;
  onHome: () => void;
}) {
  const [dieren, setDieren] = useState<PublicAnimalSummary[]>([]);
  const [staat, setStaat] = useState<"laden" | "fout" | "klaar">("laden");
  // De slugs waarmee deze pagina geopend werd. Niet `favorieten` zelf: wie hier een hartje
  // uitzet hoort het dier te zien verdwijnen zonder dat de hele lijst opnieuw opgehaald wordt.
  const [gevraagd] = useState(favorieten);

  useEffect(() => {
    if (gevraagd.length === 0) {
      setStaat("klaar");
      return;
    }
    let levend = true;
    fetchBewaard(gevraagd, "DIERMATCH")
      .then((gevonden) => {
        if (!levend) return;
        setDieren(gevonden);
        setStaat("klaar");
        const nog = gevonden.map((d) => d.slug);
        if (nog.length !== gevraagd.length) onOpvallen(nog);
      })
      .catch(() => {
        if (levend) setStaat("fout");
      });
    return () => {
      levend = false;
    };
  }, [gevraagd, onOpvallen]);

  // Wat de bezoeker hier zelf weghaalt verdwijnt meteen; wat de server niet terugstuurde is
  // geplaatst, en dat melden we apart — anders lijkt het alsof we een dier zijn kwijtgeraakt.
  const zichtbaar = dieren.filter((d) => favorieten.includes(d.slug));
  const kwijt = staat === "klaar" ? gevraagd.length - dieren.length : 0;

  return (
    <section className="dm-favorieten dm-shell">
      <button className="dm-match-terug dm-match-introterug" onClick={onHome}>← Terug</button>
      <h1 className="dm-favorieten-kop">Jouw favorieten</h1>
      <p className="dm-favorieten-onder">
        Bewaard op dit apparaat. Je hoeft er geen account voor te maken, en wij weten niet wie je bent.
      </p>

      {staat === "laden" && <p className="dm-favorieten-leeg">Je bewaarde dieren worden opgehaald…</p>}

      {staat === "fout" && (
        <p className="dm-favorieten-leeg">
          Je bewaarde dieren konden niet opgehaald worden. Probeer het zo nog eens.
        </p>
      )}

      {staat === "klaar" && kwijt > 0 && (
        <p className="dm-favorieten-weg" role="status">
          {kwijt === 1
            ? "Eén bewaard dier heeft een thuis gevonden en staat niet meer in de lijst."
            : `${kwijt} bewaarde dieren hebben een thuis gevonden en staan niet meer in de lijst.`}
        </p>
      )}

      {staat === "klaar" && zichtbaar.length === 0 && (
        <div className="dm-favorieten-leeg">
          <p>Je hebt nog geen dieren bewaard.</p>
          <p>
            Kom je een dier tegen dat je wilt onthouden, klik dan op het hartje. Het blijft hier
            staan tot je het weghaalt — of tot het dier een thuis gevonden heeft.
          </p>
          <button className="diermatch-cta" onClick={onHome}>Bekijk de dieren</button>
        </div>
      )}

      {zichtbaar.length > 0 && (
        <div className="diermatch-grid">
          {zichtbaar.map((animal) => (
            <AnimalCard
              key={animal.slug}
              animal={animal}
              favoriet={favorieten.includes(animal.slug)}
              onFavoriet={() => onFavoriet(animal.slug)}
              onOpen={() => onOpen(animal.slug)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function OpvangPagina({
  sleutel,
  insluiten,
  favorieten,
  onFavoriet,
  onOpen,
  onHome,
}: {
  sleutel: string;
  insluiten: boolean;
  favorieten: string[];
  onFavoriet: (slug: string) => void;
  onOpen: (slug: string) => void;
  onHome: () => void;
}) {
  const [opvang, setOpvang] = useState<PublicOpvang | null>(null);
  const [huisstijl, setHuisstijl] = useState<PublicHuisstijl | null>(null);
  const [staat, setStaat] = useState<"laden" | "onbekend" | "fout" | "klaar">("laden");
  const [groep, setGroep] = useState("");
  const [zoekveld, setZoekveld] = useState("");
  const [leeftijd, setLeeftijd] = useState("");
  const [geslacht, setGeslacht] = useState("");
  // De overige vragen uit de kenmerkencatalogus; pas zichtbaar als de bezoeker "Meer filters" opent,
  // zodat de filterregel niet meteen vol staat met vragen die maar een deel van de dieren beantwoordt.
  const [kenmerken, setKenmerken] = useState<Record<string, string>>({});
  const [meerOpen, setMeerOpen] = useState(false);
  const [overOpen, setOverOpen] = useState(false);
  // De ingestuurde zoekterm, niet het veld: zoeken gaat op de knop, zoals op de homepage.
  const [q, setQ] = useState("");
  const [resultaat, setResultaat] = useState<PublicSearchResult | null>(null);
  const [items, setItems] = useState<PublicAnimalSummary[]>([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [phase, setPhase] = useState<"loading" | "error" | "ready">("loading");

  // Het logo van dit asiel. DierMatch houdt zijn eigen kleuren: wie van de ene opvangpagina naar de
  // andere gaat hoort te zien dat hij op DierMatch is, en het asiel is herkenbaar aan logo, naam en tekst.
  useEffect(() => {
    let actief = true;
    fetchOpvangHuisstijl(sleutel)
      .then((h) => { if (actief) setHuisstijl(h); })
      .catch(() => { if (actief) setHuisstijl(null); });
    return () => { actief = false; };
  }, [sleutel]);

  useEffect(() => {
    let actief = true;
    setStaat("laden");
    fetchOpvangen("DIERMATCH")
      // Niet in de lijst (die kent alleen opvangen mét dieren)? Vraag de opvang zelf op: een gekoppeld asiel
      // zonder dieren krijgt dan zijn pagina met "Op dit moment geen dieren beschikbaar".
      .then(async (alle) => alle.find((o) => o.sleutel === sleutel) ?? (await fetchOpvang(sleutel, "DIERMATCH")))
      .then((gevonden) => {
        if (!actief) return;
        setOpvang(gevonden);
        setStaat(gevonden ? "klaar" : "onbekend");
      })
      .catch(() => {
        if (actief) setStaat("fout");
      });
    return () => {
      actief = false;
    };
  }, [sleutel]);

  useEffect(() => {
    if (opvang?.naam) {
      document.title = `${opvang.naam} · DierMatch`;
    }
  }, [opvang?.naam]);

  const load = useCallback(async (index: number, append: boolean) => {
    if (!append) setPhase("loading");
    try {
      const r = await fetchSearch({
        kanaal: "DIERMATCH",
        opvangId: sleutel,
        groep: groep || undefined,
        q: q || undefined,
        leeftijd: leeftijd ? [leeftijd] : undefined,
        geslacht: geslacht ? [geslacht] : undefined,
        kenmerken: Object.fromEntries(
          Object.entries(kenmerken).filter(([, w]) => w).map(([k, w]) => [k, [w]]),
        ),
        // Gereserveerde dieren blijven staan, met een lint; beschikbare komen eerst (zoekdienst sorteert zo).
        alleenBeschikbaar: false,
        page: index,
        size: PAGE_SIZE,
      });
      setResultaat(r);
      setItems((prev) => (append ? [...prev, ...r.items] : r.items));
      setPageIndex(index);
      setPhase("ready");
    } catch {
      setPhase("error");
    }
  }, [sleutel, groep, q, leeftijd, geslacht, kenmerken]);

  useEffect(() => {
    if (staat === "klaar") {
      void load(0, false);
    }
  }, [staat, load]);

  // Geen zoekknop in het ontwerp: de zoekterm gaat vanzelf mee, maar pas als het typen even stilligt.
  useEffect(() => {
    const t = window.setTimeout(() => setQ(zoekveld.trim()), 350);
    return () => window.clearTimeout(t);
  }, [zoekveld]);

  if (staat === "laden") {
    return <div className="diermatch-state"><div className="dm-state-icon"><Icoon naam="poot" size={34} /></div><p>Bezig met laden…</p></div>;
  }
  if (staat === "fout") {
    return (
      <div className="diermatch-state">
        <div className="dm-state-icon"><Icoon naam="hart" size={34} /></div>
        <p>De dieren van deze opvang konden niet worden opgehaald. Probeer het zo nog eens.</p>
      </div>
    );
  }
  if (staat === "onbekend" || !opvang) {
    return (
      <div className="diermatch-state">
        <div className="dm-state-icon"><Icoon naam="zoek" size={34} /></div>
        <h2>Deze opvang kennen we niet</h2>
        <p>Het adres klopt niet, of deze opvang heeft op dit moment geen dieren op DierMatch staan.</p>
        {!insluiten && <button className="diermatch-chip-btn" onClick={onHome}>Bekijk alle dieren</button>}
      </div>
    );
  }

  const totaal = resultaat?.totalElements ?? 0;
  const hasMore = resultaat ? items.length < resultaat.totalElements : false;
  const gefilterd = Boolean(groep || q || leeftijd || geslacht || Object.values(kenmerken).some(Boolean));
  const groepen = (resultaat?.groepen ?? []).filter((g) => g.aantal > 0 || g.pad === groep);
  const facetten = resultaat?.facetten ?? [];
  const leeftijden = facetten.find((f) => f.sleutel === "leeftijd")?.opties ?? [];
  const geslachten = facetten.find((f) => f.sleutel === "geslacht")?.opties ?? [];
  // Alles buiten de vier vaste facetten is een vraag uit de catalogus — die horen onder "Meer filters".
  const extraFacetten = facetten.filter((f) => !VASTE_FACETTEN.includes(f.sleutel));
  const wisAlles = () => {
    setGroep("");
    setZoekveld("");
    setQ("");
    setLeeftijd("");
    setGeslacht("");
    setKenmerken({});
  };

  return (
    <section className="dm-opvang-pagina dm-shell" id="dieren">
      {!insluiten && (
        <>
          <button className="diermatch-back" onClick={onHome}>← Alle dieren op DierMatch</button>
          {/* De kop van de opvang: logo, naam en tekst links, het beeld loopt door tot de rand en
              vervaagt in het groen, met de teller er als rondje overheen. In de kleuren van DierMatch
              — het asiel is herkenbaar aan logo, naam en tekst, niet aan een eigen palet. */}
          <header className="dm-opvang-hero">
            <div className="dm-opvang-hero__beeld" aria-hidden="true">
              <img src={OPVANG_BAND} alt="" />
            </div>
            <div className="dm-opvang-hero__merk">
              {huisstijl?.logoUrl ? (
                <img className="dm-asiel-logo dm-asiel-logo--beeld" src={huisstijl.logoUrl} alt="" />
              ) : (
                <span className="dm-asiel-logo" aria-hidden="true">{initialen(opvang.naam)}</span>
              )}
            </div>
            <div className="dm-opvang-hero__tekst">
              <h1>{opvang.naam ?? "Opvang"}</h1>
              {opvang.plaats && <p className="dm-opvang-hero__plaats"><Icoon naam="speld" size={14} /> {opvang.plaats}</p>}
              <p className="dm-opvang-hero__intro">
                Bij {opvang.naam} zetten we ons elke dag in voor dieren die een nieuw thuis zoeken.
                Van honden en katten tot kleine huisdieren. Ieder dier verdient een tweede kans.
              </p>
              <div className="dm-opvang-hero__acties">
                <button
                  type="button"
                  className="dm-opvang-hero__knop"
                  aria-expanded={overOpen}
                  onClick={() => setOverOpen((o) => !o)}
                >
                  Over deze opvang
                </button>
                <button
                  type="button"
                  className="dm-opvang-hero__knop dm-opvang-hero__knop--licht"
                  onClick={() => {
                    setOverOpen(true);
                    // Pas ná het openen bestaat het blok; vandaar de sprong in een volgende tik.
                    window.setTimeout(
                      () => document.getElementById("opvang-contact")?.scrollIntoView({ block: "center" }),
                      0,
                    );
                  }}
                >
                  <Icoon naam="envelop" size={15} /> Contact
                </button>
              </div>
            </div>
            {phase === "ready" && (
              <div className="dm-opvang-hero__teller" aria-label={`${totaal} dieren zoeken een thuis`}>
                <TellerRand />
                <strong>{totaal}</strong>
                <span>{totaal === 1 ? "dier zoekt" : "dieren zoeken"} een thuis</span>
              </div>
            )}
          </header>

          {overOpen && (
            <div className="dm-opvang-over">
              <div>
                <h3>Over {opvang.naam}</h3>
                <p>
                  {opvang.naam} vangt dieren op die om uiteenlopende redenen niet meer thuis kunnen
                  blijven: gevonden dieren, afstandsdieren, en dieren waarvan de eigenaar niet langer
                  voor ze kan zorgen. Elk dier wordt medisch nagekeken en krijgt de tijd om tot rust te
                  komen voordat het opnieuw geplaatst wordt.
                </p>
              </div>
              <div id="opvang-contact">
                <h3>Contact</h3>
                <p>
                  Kennismaken en adopteren gaat altijd via de opvang zelf — zij kennen het dier het
                  beste. Op de pagina van het dier waar je in geïnteresseerd bent staat hoe je
                  {opvang.plaats ? ` ${opvang.naam} in ${opvang.plaats}` : ` ${opvang.naam}`} bereikt.
                </p>
              </div>
            </div>
          )}

          <div className="dm-opvang-titel">
            <div>
              <h2>Dieren bij deze opvang</h2>
              <p>Bekijk alle dieren die op dit moment bij {opvang.naam} een nieuw thuis zoeken.</p>
            </div>
            <p className="dm-opvang-melding">
              <Icoon naam="schild" size={15} /> Je bekijkt alleen de dieren van {opvang.naam}.
            </p>
          </div>
        </>
      )}

      {/* Een rij pillen zonder zoekknop, zoals het ontwerp: elke keuze zoekt meteen. De zoekterm
          wacht kort af, anders vuurt elke aanslag een verzoek af. */}
      <form
        className="dm-opvang-filters"
        onSubmit={(e) => {
          e.preventDefault();
          setQ(zoekveld.trim());
        }}
      >
        <span className="dm-opvang-filters__pil">
          <i><Icoon naam="poot" size={16} /></i>
          <select value={groep} onChange={(e) => setGroep(e.target.value)} aria-label="Diersoort">
            <option value="">Alle diersoorten</option>
            {groepen.map((g) => <option key={g.pad} value={g.pad}>{g.label} ({g.aantal})</option>)}
          </select>
          <Icoon naam="chevron" size={16} />
        </span>
        <span className="dm-opvang-filters__pil dm-opvang-filters__pil--zoek">
          <i><Icoon naam="zoek" size={16} /></i>
          <input
            value={zoekveld}
            onChange={(e) => setZoekveld(e.target.value)}
            placeholder="Naam of ras zoeken…"
            aria-label="Zoek op naam of ras"
          />
        </span>
        <span className="dm-opvang-filters__pil">
          <select
            value={leeftijd}
            onChange={(e) => setLeeftijd(e.target.value)}
            disabled={leeftijden.length === 0}
            aria-label="Leeftijd"
          >
            <option value="">Alle leeftijden</option>
            {leeftijden.map((o) => <option key={o.waarde} value={o.waarde}>{o.label} ({o.aantal})</option>)}
          </select>
          <Icoon naam="chevron" size={16} />
        </span>
        <span className="dm-opvang-filters__pil">
          <select
            value={geslacht}
            onChange={(e) => setGeslacht(e.target.value)}
            disabled={geslachten.length === 0}
            aria-label="Geslacht"
          >
            <option value="">Alle geslachten</option>
            {geslachten.map((o) => <option key={o.waarde} value={o.waarde}>{o.label} ({o.aantal})</option>)}
          </select>
          <Icoon naam="chevron" size={16} />
        </span>
        {/* Zonder knop moet Enter wél werken; hij staat er alleen voor toetsenbord en schermlezer. */}
        <button type="submit" className="dm-opvang-filters__verborgen">Zoeken</button>
        {extraFacetten.length > 0 && (
          <button
            type="button"
            className="dm-opvang-filters__meer"
            aria-expanded={meerOpen}
            onClick={() => setMeerOpen((m) => !m)}
          >
            <Icoon naam="schuifjes" size={16} /> Meer filters
          </button>
        )}
        {/* Bij insluiten staat de kop niet op de pagina; dan hoort de teller hier wél te staan, anders
            weet de bezoeker niet hoeveel dieren er zijn. */}
        {insluiten && phase === "ready" && (
          <span className="dm-teller">{totaal} {totaal === 1 ? "dier zoekt" : "dieren zoeken"} een thuis</span>
        )}
        {meerOpen && extraFacetten.length > 0 && (
          <div className="dm-opvang-filters__extra">
            {extraFacetten.map((f) => (
              <label key={f.sleutel}>
                <span>{f.label}</span>
                <select
                  value={kenmerken[f.sleutel] ?? ""}
                  onChange={(e) => setKenmerken((k) => ({ ...k, [f.sleutel]: e.target.value }))}
                  aria-label={f.label}
                >
                  <option value="">Maakt niet uit</option>
                  {f.opties.map((o) => (
                    <option key={o.waarde} value={o.waarde}>{o.label} ({o.aantal})</option>
                  ))}
                </select>
              </label>
            ))}
          </div>
        )}
      </form>

      {phase === "loading" && (
        <div className="diermatch-skeleton" aria-hidden="true">
          {Array.from({ length: 5 }, (_, i) => <div className="dm-skeleton-card" key={i} />)}
        </div>
      )}
      {phase === "error" && (
        <p className="dm-melding">De dieren konden niet worden geladen. Probeer het zo weer.</p>
      )}
      {phase === "ready" && items.length === 0 && (
        <div className="dm-leeg">
          <span className="dm-leeg-icoon"><Icoon naam="poot" size={31} /></span>
          <div>
            <h3>{gefilterd ? "Geen dieren gevonden" : "Op dit moment geen dieren beschikbaar"}</h3>
            <p>{gefilterd
              ? "Probeer een andere diersoort of zoekterm."
              : "Kijk binnenkort nog eens: nieuwe dieren komen hier te staan zodra de opvang ze plaatst."}</p>
          </div>
          {gefilterd && (
            <button type="button" onClick={wisAlles}>Wis filters</button>
          )}
        </div>
      )}

      {items.length > 0 && (
        <div className="diermatch-grid">
          {items.map((animal) => (
            <AnimalCard
              key={animal.platformId}
              animal={animal}
              favoriet={favorieten.includes(animal.slug)}
              onFavoriet={() => onFavoriet(animal.slug)}
              onOpen={() => onOpen(animal.slug)}
            />
          ))}
        </div>
      )}

      {hasMore && (
        <div className="dm-meer">
          <button className="dm-meer-knop" onClick={() => void load(pageIndex + 1, true)}>
            Meer dieren laden
          </button>
        </div>
      )}

      {/* Onderaan terug naar het grote aanbod: een bezoeker die hier niets vindt hoort niet op een
          doodlopende pagina te eindigen. Niet bij insluiten — daar staat de pagina in de site van het
          asiel zelf en hoort er geen verwijzing van ons doorheen. */}
      {!insluiten && (
        <aside className="dm-opvang-slot">
          <span className="dm-opvang-slot__beeld" aria-hidden="true">
            <img src={HERO_FOTO} alt="" />
          </span>
          <span className="dm-opvang-slot__pootjes" aria-hidden="true">
            <Icoon naam="poot" size={30} />
            <Icoon naam="poot" size={22} />
          </span>
          <div>
            <h3>Nog niet jouw match gevonden?</h3>
            <p>
              Bekijk alle dieren op DierMatch en ontdek nog veel meer honden, katten, konijnen en andere
              huisdieren die een nieuw thuis zoeken, bij opvangcentra in heel Nederland.
            </p>
          </div>
          <button className="dm-opvang-slot__knop" onClick={onHome}>
            <span className="dm-opvang-slot__knop-icoon"><Icoon naam="zoek" size={16} /></span>
            Zoek tussen alle dieren op DierMatch
            <Icoon naam="pijlRechts" size={17} />
          </button>
        </aside>
      )}
    </section>
  );
}

// ── Detail ───────────────────────────────────────────────────────────────────────────────────────

function Detail({ slug, onBack, terugTekst = "Terug", favoriet = false, onFavoriet }: {
  slug: string;
  onBack: () => void;
  terugTekst?: string;
  /** Of dit dier in de bewaarde lijst van deze bezoeker staat. */
  favoriet?: boolean;
  /** Bewaren of weer loslaten. Zonder deze functie toont de pagina de knop niet. */
  onFavoriet?: () => void;
}) {
  const [animal, setAnimal] = useState<PublicAnimalDetail | null>(null);
  const [phase, setPhase] = useState<"loading" | "notfound" | "error" | "ready">("loading");
  const [activePhoto, setActivePhoto] = useState(0);
  const [galleryOpen, setGalleryOpen] = useState(false);
  /**
   * De kennismakingsvragen van dit asiel. `null` betekent "geen lijst voor deze diersoort", en dan
   * blijft de knop doen wat hij altijd deed: naar het contactblok springen. Een asiel zonder
   * vragenlijst hoort niet onbereikbaar te worden.
   */
  const [kennisLijst, setKennisLijst] = useState<KennismakingLijst | null>(null);
  const [kennisOpen, setKennisOpen] = useState(false);

  /*
   * Tel dat hier iemand naar dit dier heeft gekeken.
   *
   * <p>Dit deed DierMatch tot nu toe helemaal niet: alleen ikzoekbaas telde. In de backoffice stond
   * daardoor bij DierMatch een cijfer dat van de andere site kwam -- hetzelfde getal op allebei de
   * tabbladen, over geen van beide waar (Maarten, 30-10-2026). De zeven staan in useTelWeergave en
   * zijn voor allebei de sites dezelfde.
   */
  useTelWeergave(animal?.slug, "DIERMATCH");

  useEffect(() => {
    let active = true;
    setKennisLijst(null);
    setKennisOpen(false);
    void (async () => {
      try {
        const lijst = await fetchKennismaking(slug);
        if (active) {
          setKennisLijst(lijst);
        }
      } catch {
        // Stil: het formulier is een extra. Valt het weg, dan staat het contactblok er nog.
      }
    })();
    return () => {
      active = false;
    };
  }, [slug]);

  useEffect(() => {
    let active = true;
    setPhase("loading");
    void (async () => {
      try {
        const result = await fetchPublication(slug, "DIERMATCH");
        if (active) {
          setAnimal(result);
          setActivePhoto(0);
          setGalleryOpen(false);
          setPhase("ready");
        }
      } catch (error) {
        if (active) {
          setPhase(statusOf(error) === 404 ? "notfound" : "error");
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [slug]);

  useEffect(() => {
    document.title = animal ? `${animal.naam} · DierMatch` : "DierMatch";
  }, [animal]);

  if (phase === "loading") {
    return <div className="diermatch-state"><div className="dm-state-icon"><Icoon naam="poot" size={34} /></div><p>Bezig met laden…</p></div>;
  }
  if (phase === "notfound") {
    return (
      <div className="diermatch-state">
        <div className="dm-state-icon"><Icoon naam="zoek" size={34} /></div>
        <p>Dit dier is niet (meer) beschikbaar.</p>
        <button className="diermatch-chip-btn" onClick={onBack}>Terug naar het overzicht</button>
      </div>
    );
  }
  if (phase === "error" || !animal) {
    return (
      <div className="diermatch-state">
        <div className="dm-state-icon"><Icoon naam="hart" size={34} /></div>
        <p>Er ging iets mis. Probeer het later opnieuw.</p>
        <button className="diermatch-chip-btn" onClick={onBack}>Terug naar het overzicht</button>
      </div>
    );
  }

  const facts: Array<{ label: string; value: string; icon: string }> = [
    animal.leeftijd ? { label: "Leeftijd", value: animal.leeftijd, icon: "kalender" } : null,
    animal.geslacht ? { label: "Geslacht", value: animal.geslacht, icon: "mensen" } : null,
    animal.ras ? { label: "Ras", value: animal.ras, icon: "poot" } : null,
    animal.soort ? { label: "Soort", value: animal.soort, icon: "poot" } : null,
  ].filter(Boolean) as Array<{ label: string; value: string; icon: string }>;
  const heroPhoto = animal.fotos[activePhoto] ?? animal.fotos[0];
  const zichtbareFotos = galleryOpen ? animal.fotos : animal.fotos.slice(0, 6);
  const kenmerkRijen = kenmerkRijenVoor(animal);
  const bezet = nietBeschikbaar(animal.beschikbaarheid);
  // Een geïmporteerde publicatie kan nog de technische platformorganisatie in `opvangNaam` dragen,
  // terwijl `contact` en `locatie` de opvang zijn die het dier daadwerkelijk aanbiedt. Voor bezoekers
  // telt uitsluitend die publieke contactopvang; daarom staat de platformorganisatie bewust als laatste.
  const asielNaam = animal.contact?.naam ?? animal.locatie ?? animal.opvangNaam;

  return (
    <div className="diermatch-detail-shell dm-shell">
      <button className="diermatch-back" onClick={onBack}>← {terugTekst}</button>
      <div className="diermatch-detail">
        <aside className="dm-detail-summary">
          {/* Een gereserveerd dier moet je in één oogopslag als gereserveerd zien: een klein regeltje boven de naam
              viel niet op (Maarten, 2026-10-11). Daarom een duidelijk statuslabel hier, en hetzelfde lint op de foto. */}
          {bezet ? (
            <span className="dm-detail-status" role="status">{animal.beschikbaarheid}</span>
          ) : (
            <span className="dm-detail-eyebrow">Beschikbaar voor adoptie</span>
          )}
          <h1>{animal.naam}</h1>
          {animal.tags.length > 0 && (
            <div className="diermatch-tags" style={{ marginBottom: 14 }}>
              {animal.tags.map((tag) => (
                <span key={tag} className="diermatch-tag">{tag}</span>
              ))}
            </div>
          )}
          {asielNaam && <p className="diermatch-location"><Icoon naam="speld" size={16} /> {asielNaam}</p>}
          {animal.intro && <p className="diermatch-intro">{animal.intro}</p>}
          {facts.length > 0 && (
            <div className="diermatch-facts">
              {facts.map((f) => (
                <div className="diermatch-fact" key={f.label}>
                  <span className="dm-fact-icon"><Icoon naam={f.icon} size={21} /></span>
                  <span>
                    <span className="dm-fact-label">{f.label}</span>
                    <span className="dm-fact-value">{f.value}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
          <button
            className="diermatch-cta"
            onClick={() => {
              if (kennisLijst && kennisLijst.vragen.length > 0) {
                // Het formulier scrollt zichzelf in beeld, bij het openen en bij elke stap. Hier ook
                // scrollen gaf twee scrolls over elkaar heen, en dan kwam je honderden pixels onder
                // de eerste vraag uit (gemeten 30-10-2026).
                setKennisOpen(true);
              } else {
                scrollNaar("contact");
              }
            }}
          >
            <Icoon naam="hart" size={21} /> Ik wil {animal.naam} leren kennen
          </button>
          {onFavoriet && (
            /* Bewaren is geen kennismaking: een eigen knop, rustiger dan de hoofdknop, zodat
               niemand per ongeluk het asiel benadert terwijl hij alleen nog aan het kijken is. */
            <button
              type="button"
              className={favoriet ? "dm-detail-bewaar is-actief" : "dm-detail-bewaar"}
              onClick={onFavoriet}
              aria-pressed={favoriet}
            >
              <span aria-hidden="true">{favoriet ? "♥" : "♡"}</span>
              {favoriet ? `${animal.naam} staat in je favorieten` : `Bewaar ${animal.naam}`}
            </button>
          )}
          {bezet ? (
            <p className="dm-detail-bezet">
              <strong>{animal.beschikbaarheid}.</strong> Je kunt je interesse nog wel doorgeven aan het asiel.
            </p>
          ) : (
            <p className="dm-detail-trust"><Icoon naam="schild" size={15} /> Kennismaken verloopt veilig en rechtstreeks via het asiel.</p>
          )}
        </aside>

        <div className="diermatch-gallery">
          <div className="dm-hero-frame">
            {bezet && <span className="dm-status-lint dm-status-lint--groot">{animal.beschikbaarheid}</span>}
            <Photo src={heroPhoto?.url ?? null} alt={heroPhoto?.alt ?? animal.naam} />
          </div>
          {animal.fotos.length > 1 && (
            <div className="dm-gallery-tray">
              <div className="dm-gallery-kop">
                <strong>Fotogalerij</strong>
                <span>{animal.fotos.length} {animal.fotos.length === 1 ? "foto" : "foto’s"}</span>
                {galleryOpen && animal.fotos.length > 6 && <button type="button" onClick={() => setGalleryOpen(false)}>Minder tonen</button>}
              </div>
              <div className="diermatch-thumbs">
                {zichtbareFotos.map((foto, i) => {
                  const isMeerKnop = !galleryOpen && i === 5 && animal.fotos.length > 6;
                  return (
                    <button
                      key={foto.url}
                      aria-current={!isMeerKnop && i === activePhoto}
                      onClick={() => {
                        if (isMeerKnop) setGalleryOpen(true);
                        else setActivePhoto(i);
                      }}
                      aria-label={isMeerKnop ? `Toon ${animal.fotos.length - 5} extra foto’s` : `Foto ${i + 1}`}
                    >
                      <img src={foto.url} alt={foto.alt ?? `${animal.naam} foto ${i + 1}`} loading="lazy" />
                      {i === 0 && <span className="dm-hoofdfoto-label">Hoofd</span>}
                      {isMeerKnop && <span className="dm-meer-fotos">+{animal.fotos.length - 5}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="dm-detail-verhaal">
          {animal.overDitDier && (
            <section className="diermatch-section dm-detail-over">
              <h2>Over {animal.naam}</h2>
              <p>{animal.overDitDier}</p>
            </section>
          )}

          {kenmerkRijen.length > 0 && (
            <section className="diermatch-section">
              <h2>Kenmerken</h2>
              <ul className="diermatch-kenmerken">
                {kenmerkRijen.map((r) => (
                  <li key={r.sleutel} className={r.soort === "nee" ? "dm-kenmerk--nee" : undefined}>
                    <span className="dm-check" aria-hidden="true">{r.soort === "nee" ? "✕" : r.soort === "ja" ? "✓" : "•"}</span>
                    <span className="dm-kenmerk-tekst">{r.tekst}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {kennisOpen && kennisLijst && (
            <div id="kennismaking" className="dm-kennis-plek">
              <DierMatchKennismaking
                slug={slug}
                lijst={kennisLijst}
                dierNaam={animal.naam}
                asielNaam={asielNaam ?? null}
                dierFoto={animal.fotos[0] ?? null}
                onSluit={() => setKennisOpen(false)}
              />
            </div>
          )}

          <section className="diermatch-section dm-detail-contact" id="contact">
            <span className="diermatch-contact-icoon"><Icoon naam="speld" size={24} /></span>
            <div>
              <h2>Locatie &amp; contact</h2>
              {animal.contact?.adres && <p className="dm-contact-adres">{animal.contact.adres}</p>}
              <p>{animal.contact?.tekst ?? "Plan een kennismaking rechtstreeks met de opvang."}</p>
              {animal.contact && (
                <div className="diermatch-contactlinks">
                  {animal.contact.telefoon && <a href={`tel:${animal.contact.telefoon.replace(/\s/g, "")}`}>Bel het asiel</a>}
                  {animal.contact.email && <a href={`mailto:${animal.contact.email}`}>Stuur een e-mail</a>}
                  {animal.contact.url && <a href={animal.contact.url} target="_blank" rel="noreferrer">Website</a>}
                </div>
              )}
            </div>
          </section>

        </div>

        <section className="dm-kennismaken" aria-labelledby="dm-kennismaken-titel">
          <h2 id="dm-kennismaken-titel">Zo werkt kennismaken</h2>
          <div className="dm-kennismaken-stappen">
            <div className="dm-kennismaken-stap">
              <span className="dm-stapnummer">1</span>
              <span className="dm-kennismaken-icoon"><Icoon naam="zoek" size={26} /></span>
              <div><strong>Ontdek</strong><p>Bekijk het profiel en lees alles over {animal.naam}.</p></div>
            </div>
            <div className="dm-kennismaken-stap">
              <span className="dm-stapnummer">2</span>
              <span className="dm-kennismaken-icoon"><Icoon naam="praatwolk" size={27} /></span>
              <div><strong>Kennismaken</strong><p>Neem contact op en plan een vrijblijvende kennismaking.</p></div>
            </div>
            <div className="dm-kennismaken-stap">
              <span className="dm-stapnummer">3</span>
              <span className="dm-kennismaken-icoon"><Icoon naam="huis" size={27} /></span>
              <div><strong>Adopteren</strong><p>Is er een klik? Dan begeleidt het asiel je naar een nieuw thuis.</p></div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

/**
 * De kenmerken zoals DierMatch ze toont. Daarvoor stond er bij elk gepubliceerd kenmerk een vinkje, ook als het
 * antwoord "Nee" of "Onbekend" was (Alma, Roze, 2026-10-09). Nu: Ja een vinkje, Nee een kruisje, Onbekend niet,
 * en een ander antwoord ("Vanaf 12 jaar") staat er letterlijk achter. Een publicatie met alleen sleutels houdt
 * zijn vinkjes: een sleutel zonder antwoord betekent een bevestigd kenmerk.
 */
type KenmerkRij = { sleutel: string; soort: "ja" | "nee" | "anders"; tekst: string };
export function kenmerkRijenVoor(dier: { kenmerken: string[]; kenmerkAntwoorden?: PublicKenmerk[] | null }): KenmerkRij[] {
  const antwoorden = dier.kenmerkAntwoorden ?? [];
  if (antwoorden.length === 0) {
    return dier.kenmerken.map((sleutel) => ({ sleutel, soort: "ja" as const, tekst: kenmerkLabel(sleutel) }));
  }
  return antwoorden.flatMap((k): KenmerkRij[] => {
    const antwoord = (k.antwoord ?? "").trim();
    const laag = antwoord.toLowerCase();
    const label = k.label || kenmerkLabel(k.sleutel);
    // De beschikbaarheid staat al als status boven het dier (en als lint op de kaart), niet nog eens als kenmerk.
    if (laag === "" || laag === "onbekend" || k.sleutel === "BESCHIKBAARHEID") return [];
    // "Ja", "Nee", maar ook "Nee, jachtinstinct": het eerste woord bepaalt het teken, de rest staat erachter.
    const m = /^(ja|nee)\b[\s,:;-]*(.*)$/i.exec(antwoord);
    if (m) {
      const rest = m[2].trim();
      return [{ sleutel: k.sleutel, soort: m[1].toLowerCase() as "ja" | "nee", tekst: rest ? `${label} (${rest})` : label }];
    }
    return [{ sleutel: k.sleutel, soort: "anders" as const, tekst: `${label}: ${antwoord}` }];
  });
}

/**
 * Een dier dat gereserveerd of anderszins niet vrij is ("Gereserveerd", "Nog niet beschikbaar"). Zo'n dier bleef van
 * DierMatch af; nu blijft het staan met zijn status, zoals op ikzoekbaas (Alma, Roze, 2026-10-09).
 */
export function nietBeschikbaar(beschikbaarheid: string | null | undefined): boolean {
  const b = (beschikbaarheid ?? "").trim().toLowerCase();
  return b !== "" && b !== "beschikbaar";
}
